import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useRouteMatch } from 'react-router'
import {
  useCountryData,
  useCustomer,
  useBasket,
  useNavigation,
  useCookie,
  useBraze
} from 'msp-integrations'
import {
  ShowOnDesktop,
  LanguageModal,
  EventsEmitter,
  Events
} from 'msp-components'
import { IconButton } from '@/components/molecules/IconButton'
import { UserHeaderIcon } from '@/components/molecules/UserHeaderIcon'
import JasminMiniCart from '@/components/organisms/JasminMiniCart/JasminMiniCart'
import JasminDesktopSearch from '@/components/organisms/JasminDesktopSearch/JasminDesktopSearch'
import ChoiceOfBonusProductsModal from '@/components/organisms/ChoiceOfBonusProductsModal/ChoiceOfBonusProductsModal'
import FixedHeaderWrapper from './partials/FixedHeaderWrapper'
import { HeaderProps } from './Header.types'
import { MIN_AMOUNT_FREE_SHIPPING, MIN_BOXES_FREE_DELIVERY } from '@/constants'

const defaults = {
  mobileHeaderHeight: 'h-[66px]',
  desktopHeaderHeight: 'md:h-[65px]',
  iconsHeight: '20px',
  iconsSpacing: 'space-x-[18px] md:space-x-6',
  backgroundColor: {
    transparent: 'bg-transparent',
    solid: 'bg-black'
  },
  contentColor: {
    light: 'text-white fill-white',
    dark: 'text-machine2-100 fill-machine2-100'
  }
}

const Header = ({
  regions,
  isTransparent,
  lightContent,
  onMenuClick,
  onUserClick,
  onLogoClick,
  onNotificationClick
}: HeaderProps) => {
  const { formatMessage } = useIntl()
  const { eventTracking } = useCookie()
  const i18n = useCountryData()
  const navigate = useNavigation()
  const customer = useCustomer()
  const [showMiniCart, setShowMiniCart] = useState(false)
  const [liveStatusMessage, setLiveStatusMessage] = useState('')
  const basket = useBasket()
  const { path } = useRouteMatch()
  const inCartPage = path && path.includes('/cart')
  const [showLangModal, setShowLangModal] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const addToCartEmitter = useRef(EventsEmitter)
  const isMounted = useRef(false)
  const { c_minAmountFreeShipping, c_minBoxesFreeDelivery } = basket
  const minAmountFreeShipping =
    c_minAmountFreeShipping || MIN_AMOUNT_FREE_SHIPPING
  const minBoxesFreeDelivery = c_minBoxesFreeDelivery || MIN_BOXES_FREE_DELIVERY
  const { unreadNotification } = useBraze()
  let miniCartTimeout = null as NodeJS.Timeout | null

  useEffect(() => {
    isMounted.current = true

    const subscription = addToCartEmitter.current?.subscribe(
      Events.ADD_TO_CART_SUCCESS,
      (data: any) => {
        //Message for screen readers
        if (!showMiniCart && data && data.length > 0) {
          const productNames = data?.[0]?.newProducts?.join(
            ` ${formatMessage({
              defaultMessage: 'and',
              id: 'msp.accessibility.live.aria.header.joiner_word.and'
            })} `
          )
          const liveMessage = `${formatMessage(
            {
              defaultMessage: '{productNames} added to the cart',
              id: 'msp.accessibility.live.aria.header.add_to_cart_product'
            },
            { productNames: productNames }
          )}`
          setLiveStatusMessage(liveMessage)
        }
        if (!showMiniCart && isMounted.current) {
          miniCartTimeout = setTimeout(() => {
            setShowMiniCart(true)
          }, 500)
        }
      }
    )

    return () => {
      isMounted.current = false
      subscription?.unsubscribe()
      if (miniCartTimeout) {
        clearTimeout(miniCartTimeout)
      }
    }
  }, [showMiniCart])

  const headerColors = `${
    isTransparent
      ? defaults.backgroundColor.transparent
      : defaults.backgroundColor.solid
  } ${
    isTransparent && lightContent
      ? defaults.contentColor.light
      : defaults.contentColor.dark
  }`

  const handleOpenLanguageModal = () => {
    eventTracking('header_tracking', { event_action: 'language' })
    setShowLangModal(true)
  }

  const wrapperStyles = `${defaults.mobileHeaderHeight} ${defaults.desktopHeaderHeight} ${headerColors}`

  return (
    <>
      <FixedHeaderWrapper className={wrapperStyles}>
        <div
          id='main-header'
          className={'flex h-full items-center justify-center'}
        >
          <div
            className={
              'flex flex-auto justify-start stroke-white pr-[18px] md:flex-1 md:pr-6'
            }
          >
            <IconButton
              className='cc-outline-icon-btn-white'
              iconName='Menu'
              width={defaults.iconsHeight}
              height='20px'
              onClick={onMenuClick}
              event_name='header_tracking'
              event_action='burgermenu'
              data-cy={'hamburger-header-button'}
              ariaLabel={formatMessage({
                defaultMessage: 'Open menu',
                id: 'msp.accessibility.aria.label.header.menu'
              })}
            />
          </div>

          <div
            className='w-auto flex-none cursor-pointer justify-self-center fill-white'
            title='Avoury | The Tea'
          >
            <IconButton
              className='cc-outline-icon-btn-white'
              iconName='JasminLogo'
              onClick={onLogoClick}
              height='40px'
              width='150px'
              event_name='header_tracking'
              event_action='logo'
              ariaLabel={formatMessage({
                defaultMessage: 'Go to homepage',
                id: 'msp.accessibility.aria.label.header.logo'
              })}
            />
          </div>

          <div
            className={`flex flex-auto items-center justify-end stroke-white md:flex-1 ${defaults.iconsSpacing}`}
          >
            <ShowOnDesktop>
              <IconButton
                className='cc-outline-icon-btn-white'
                iconName='Search'
                onClick={() => setShowSearch(true)}
                height={defaults.iconsHeight}
                width={defaults.iconsHeight}
                event_name='header_tracking'
                event_action='search'
                data-cy={'search-button-header'}
                ariaLabel={formatMessage({
                  defaultMessage: 'Search',
                  id: 'msp.accessibility.aria.label.header.search'
                })}
              />
            </ShowOnDesktop>
            <UserHeaderIcon
              className='fill-white stroke-white text-white'
              onClick={onUserClick}
              iconSize={defaults.iconsHeight}
              isAuthenticated={customer && customer.isRegistered}
              customerName={
                customer && customer.isRegistered
                  ? `${customer.firstName} ${customer.lastName}`
                  : ''
              }
            />
            <IconButton
              className='cc-outline-icon-btn-white h-5 w-5'
              iconName={'Wishlist'}
              onClick={() => navigate('/account/wishlist')}
              height={'18px'}
              width={'18px'}
              event_name='header_tracking'
              event_action='wishlist'
              ariaLabel={formatMessage({
                defaultMessage: 'Wishlist',
                id: 'msp.accessibility.aria.label.header.wishlist'
              })}
            />
            <IconButton
              className='cc-outline-icon-btn-white'
              iconName={'Basket'}
              onClick={() => setShowMiniCart(true)}
              height={'22px'}
              width={'22px'}
              event_name='header_tracking'
              event_action='cart'
              data-cy={'basket-button-header'}
              ariaLabel={formatMessage({
                defaultMessage: 'Basket',
                id: 'msp.accessibility.aria.label.header.basket'
              })}
            />
            <div className='relative'>
              {unreadNotification && (
                <div className='absolute right-0 top-0'>
                  <IconButton
                    iconName={'BellNotificationIndicator'}
                    onClick={onNotificationClick}
                    height={'11px'}
                    width={'11px'}
                    notSelectable
                  />
                </div>
              )}
              <IconButton
                className='cc-outline-icon-btn-white'
                iconName={'BellEmpty'}
                onClick={onNotificationClick}
                height={'22px'}
                width={'22px'}
                ariaLabel={formatMessage({
                  defaultMessage: 'Notifications',
                  id: 'msp.accessibility.aria.label.header.notifications'
                })}
              />
            </div>
            <ShowOnDesktop>
              <button
                className={'cc-outline-icon-btn-white h-5'}
                onClick={() => handleOpenLanguageModal()}
                data-cy={'language-switch-button-header'}
                aria-label={formatMessage({
                  defaultMessage: 'Language selection',
                  id: 'msp.accessibility.aria.label.header.language_selection'
                })}
              >
                <div className='flex w-fit items-center rounded-[9px] border border-white bg-transparent px-[9px] py-[3px] outline-none'>
                  <p className='text-10 tracking-0 font-light uppercase leading-normal text-white'>
                    {i18n.locale?.split('_')[0]}
                  </p>
                </div>
              </button>
            </ShowOnDesktop>
            <LanguageModal
              countries={i18n.countries}
              locale={i18n.locale}
              show={showLangModal}
              setShow={setShowLangModal}
            />
          </div>
        </div>
      </FixedHeaderWrapper>
      <ShowOnDesktop className='relative'>
        <JasminDesktopSearch
          showSearch={showSearch}
          setShowSearch={setShowSearch}
          isDarkMode
        />
      </ShowOnDesktop>
      <div
        role='alert'
        aria-atomic='true'
        aria-live='assertive'
        className={'sr-only'}
      >
        {liveStatusMessage}
      </div>
      <JasminMiniCart
        showMiniCart={inCartPage ? false : showMiniCart}
        setShowMiniCart={setShowMiniCart}
        basket={basket}
        minAmountFreeShipping={minAmountFreeShipping}
        minBoxesFreeDelivery={minBoxesFreeDelivery}
      />
      <ChoiceOfBonusProductsModal />
    </>
  )
}

export default Header
